import React, { useCallback } from "react";
import { useEffect, useState } from "react";
import { apiFundingDocsIndex, apiFundingDocsUpload, apiFundingCancel } from "../lib/api";
import { Link, useNavigate, useParams } from "react-router-dom";

export default function ComplianceFundingUpload() {
  const { id } = useParams();
  const fundingId = Number(id);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);
  const [pof, setPof] = useState<File | undefined>();
  const [sow, setSow] = useState<File | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const res = await apiFundingDocsIndex(fundingId);
    setPayload(res);
    setLoading(false);
  }, [fundingId]);

  useEffect(() => { refresh(); }, [refresh]);

  async function onUpload(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!pof && !sow) {
      setError("Please select at least one file to upload.");
      return;
    }
    await apiFundingDocsUpload(fundingId, { pof_document: pof, sow_document: sow });
    setPof(undefined);
    setSow(undefined);
    setMessage("Document(s) uploaded and saved.");
    navigate(`/funding/${fundingId}`);
  }

  async function onCancel() {
    setError(null);
    setMessage(null);
    try {
      await apiFundingCancel(fundingId);
      navigate("/funding");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Unable to cancel.");
    }
  }

  if (loading) return <div className="section-box skeleton">Loading…</div>;

  const { funding, docs } = payload;

  return (
    <div className="max-w-3xl mx-auto card-box space-y-6">
      <header>
        <h2 className="text-xl font-bold">Upload PoF / SoW for Funding</h2>
        <p className="text-sm text-gray-600">
          Funding reference: <span className="font-mono">{funding?.reference}</span>
        </p>
      </header>

      {error && <div className="alert-danger">{error}</div>}
      {message && <div className="alert-success">{message}</div>}

      <form onSubmit={onUpload} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Proof of Funds (PDF/JPG/PNG, max 10 MB)</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setPof(e.target.files?.[0])} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Source of Wealth (PDF/JPG/PNG, max 10 MB)</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setSow(e.target.files?.[0])} />
        </div>
        <div className="flex gap-2">
          <button className="btn" type="submit">Upload</button>
          <button className="btn btn-white" type="button" onClick={onCancel}>Cancel Request</button>
          <Link to={`/funding/${fundingId}`} className="btn btn-white">Back</Link>
        </div>
      </form>

      <section>
        <h3 className="text-lg font-semibold mb-2">Existing Documents</h3>
        {(!docs || docs.length === 0) ? (
          <div className="alert-info">No documents uploaded yet.</div>
        ) : (
          <ul className="space-y-2">
            {docs.map((d: any) => (
              <li key={d.id} className="p-3 border rounded flex items-center justify-between">
                <div>
                  <div className="text-sm"><strong>Type:</strong> {d.type.toUpperCase()}</div>
                  <div className="text-xs text-gray-600"><strong>Status:</strong> {d.status}</div>
                </div>
                <div className="text-xs text-gray-500">{new Date(d.created_at).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
